/**
 * Date Formatting Utilities for Email
 *
 * Smart date formatting:
 * - Today: "Today 09:35"
 * - Yesterday: "Yesterday 21:40"
 * - This week: "Mon 14:22"
 * - Older: "Jan 15" or "Dec 3, 2025" (if different year)
 */

export function formatEmailDate(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();

  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Format time as HH:MM
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Today
  if (dateDay.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  }

  // Yesterday
  if (dateDay.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  }

  // Within last 7 days - show day name
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  if (dateDay.getTime() > weekAgo.getTime()) {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${dayName} ${timeStr}`;
  }

  // Same year - show Month Day
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  // Different year - show full date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatEmailDate(date);
}
